import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { aboutInfo, siteConfig } from '@/mocks/data';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: aboutInfo.intro,
    url: `${siteConfig.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    about: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      description: aboutInfo.intro,
      foundingDate: siteConfig.foundingDate,
      contactPoint: {
        '@type': 'ContactPoint',
        email: siteConfig.contactEmail,
        contactType: 'customer service',
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们"
        description={aboutInfo.intro}
        keywords="关于我们,看片网站,品牌介绍,联系方式,影视推荐"
        canonicalUrl={`${siteConfig.url}/about`}
        schema={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="relative w-full py-16 md:py-24 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-bold text-foreground-900 font-heading mb-4">
                  关于我们
                </h1>
                <p className="text-xl md:text-2xl text-primary-600 font-heading mb-6">
                  {aboutInfo.slogan}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.intro}
                </p>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 md:p-8 bg-background-50 rounded-2xl border border-background-200/70">
                    <div className="text-3xl md:text-4xl font-bold text-primary-600 font-heading mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="max-w-3xl mx-auto mb-16 md:mb-20">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6 text-center">
                  我们的故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.story}
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">
                  核心价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                  {aboutInfo.values.map((value) => (
                    <div key={value.title} className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                        <i className="ri-check-double-line text-xl text-accent-600"></i>
                      </div>
                      <h3 className="text-lg font-bold text-foreground-900 font-heading mb-3">
                        {value.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-500">
                  有任何问题、建议或合作意向，欢迎随时与我们联系
                </p>
              </div>

              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
                <div className="space-y-5 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{siteConfig.contactEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-500">响应时间</p>
                      <p className="text-sm font-medium text-foreground-900">我们会在24小时内回复您的邮件</p>
                    </div>
                  </div>
                </div>

                <form
                  id="about-contact-form"
                  data-readdy-form="true"
                  action="https://readdy.ai/api/form/d9rg50nnahb7382ovm30"
                  method="POST"
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      alert('提交成功！感谢您的留言，我们会尽快回复。');
                      form.reset();
                      return;
                    }
                    try {
                      const formData = new FormData(form);
                      formData.delete('website_alt');
                      const res = await fetch(form.action, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      });
                      const responseText = await res.text();
                      let parsed;
                      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                      if (res.ok && parsed?.code === 'OK') {
                        alert('提交成功！感谢您的留言，我们会尽快回复。');
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
                        alert(serverMsg);
                      }
                    } catch {
                      alert('网络错误，请稍后再试');
                    }
                  }}
                >
                  <div className="honeypot-field">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请描述您的问题或建议（最多500字）"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}